/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useDynamicDestinationForm.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Hook for managing Dynamic Destination form state and submission.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useNotifications } from '../../../hooks/useNotifications';
import { useI18n } from '../../../hooks/useI18n';
import { createDynamicDestination, updateDynamicDestination } from '../api/dynamicDestinationsApi';
import { FormState, DynamicDestinationOptionFormData, ValidationErrors, OptionErrors } from '../types';
import { validateDynamicDestinationForm } from '../utils/validators';
import { getDefaultFormState, deepClone, createEmptyOption } from '../utils/formDefaults';
import { updateOptionPriorities, sanitizeFormData, mapFormOptionToApi } from '../utils/optionHelpers';

/**
 * Hook for Dynamic Destination form management
 */
export function useDynamicDestinationForm(
  initialFormData: FormState | null,
  id: string | undefined,
  isEditMode: boolean
) {
  const { showSuccess, showError } = useNotifications();
  const { t } = useI18n();
  const showErrorRef = useRef(showError);
  const lastSaveRef = useRef<number>(0);

  const defaultFormState = useMemo(() => getDefaultFormState(), []);
  const [formData, setFormData] = useState<FormState>(
    initialFormData || defaultFormState
  );
  const [originalData, setOriginalData] = useState<FormState>(
    initialFormData || defaultFormState
  );
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [optionErrors, setOptionErrors] = useState<OptionErrors[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  // Sync with initial data when it changes
  useEffect(() => {
    if (initialFormData) {
      const currentSerialized = JSON.stringify(initialFormData);
      const lastSerialized = JSON.stringify(originalData);

      if (currentSerialized !== lastSerialized) {
        setFormData(initialFormData);
        setOriginalData(deepClone(initialFormData));
        setErrors({});
        setOptionErrors(initialFormData.options.map(() => ({})));
      }
    } else if (!isEditMode && !initialFormData) {
      // Only reset if not in edit mode and no initial data
      const defaultState = getDefaultFormState();
      setFormData(defaultState);
      setOriginalData(deepClone(defaultState));
      setErrors({});
      setOptionErrors(defaultState.options.map(() => ({})));
    }
  }, [initialFormData, isEditMode, originalData]);

  const hasChanges = useMemo(
    () => JSON.stringify(sanitizeFormData(formData)) !== JSON.stringify(sanitizeFormData(originalData)),
    [formData, originalData]
  );

  const handleFieldChange = useCallback(
    <K extends keyof FormState>(field: K, value: FormState[K]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field as string]) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[field as string];
          return next;
        });
      }
    },
    [errors]
  );

  const handleOptionChange = useCallback(
    <K extends keyof DynamicDestinationOptionFormData>(
      index: number,
      field: K,
      value: DynamicDestinationOptionFormData[K]
    ) => {
      setFormData((prev) => {
        const nextOptions = prev.options.map((option, idx) =>
          idx === index ? { ...option, [field]: value } : option
        );
        return {
          ...prev,
          options:
            field === 'priority'
              ? updateOptionPriorities(nextOptions)
              : nextOptions,
        };
      });

      setOptionErrors((prev) => {
        const next = [...prev];
        if (next[index]) {
          const optionError = { ...next[index] };
          delete optionError[field as string];
          next[index] = optionError;
        }
        return next;
      });
    },
    []
  );

  const handleDefaultModuleChange = useCallback((value: string) => {
    setFormData((prev) => ({
      ...prev,
      defaultModule: value,
      defaultDestination: '',
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.defaultModule;
      delete next.defaultDestination;
      return next;
    });
  }, []);

  const handleOptionModuleChange = useCallback((index: number, moduleValue: string) => {
    setFormData((prev) => {
      const nextOptions = prev.options.map((option, idx) =>
        idx === index
          ? {
            ...option,
            destinationModule: moduleValue,
            destinationValue: '',
          }
          : option
      );
      return { ...prev, options: nextOptions };
    });
    setOptionErrors((prev) => {
      const next = [...prev];
      const updated = { ...(next[index] ?? {}) };
      delete updated.destinationModule;
      delete updated.destinationValue;
      next[index] = updated;
      return next;
    });
  }, []);

  const addOption = useCallback(() => {
    setFormData((prev) => {
      const nextPriority = prev.options.length + 1;
      return {
        ...prev,
        options: [...prev.options, createEmptyOption(nextPriority)],
      };
    });
    setOptionErrors((prev) => [...prev, {}]);
  }, []);

  const removeOption = useCallback((index: number) => {
    setFormData((prev) => {
      const filtered = prev.options.filter((_, idx) => idx !== index);
      return {
        ...prev,
        options: filtered.length > 0 ? updateOptionPriorities(filtered) : [createEmptyOption(1)],
      };
    });
    setOptionErrors((prev) => {
      const next = prev.filter((_, idx) => idx !== index);
      return next.length > 0 ? next : [{}];
    });
  }, []);

  const validateForm = useCallback(() => {
    const { errors: validationErrors, optionErrors: validationOptionErrors } = validateDynamicDestinationForm(formData, t);
    setErrors(validationErrors);
    setOptionErrors(validationOptionErrors);

    const hasOptionErrors = validationOptionErrors.some(
      (error) => error && Object.keys(error).length > 0
    );

    return Object.keys(validationErrors).length === 0 && !hasOptionErrors;
  }, [formData, t]);

  // Handle submit - Always-Active Save Standard: always attempt to save
  const handleSubmit = useCallback(async (): Promise<FormState | undefined> => {
    if (!validateForm()) {
      showErrorRef.current(t('common.validationError'));
      return undefined;
    }

    const now = Date.now();
    if (now - lastSaveRef.current < 1000) {
      return undefined;
    }
    lastSaveRef.current = now;

    setIsSaving(true);
    try {
      const sanitized = sanitizeFormData(formData);
      const payload = {
        name: sanitized.name,
        sourceType: sanitized.sourceType,
        sourceConfig: sanitized.sourceConfig,
        queryTemplate: sanitized.queryTemplate || null,
        matchField: sanitized.matchField,
        defaultModule: sanitized.defaultModule,
        defaultDestination: sanitized.defaultDestination,
        description: sanitized.description || null,
        active: sanitized.active,
        options: sanitized.options.map(mapFormOptionToApi),
      };

      const result = isEditMode
        ? await updateDynamicDestination(id!, payload)
        : await createDynamicDestination(payload);

      showSuccess(
        isEditMode
          ? t('dynamicDestination.messages.updateSuccess')
          : t('dynamicDestination.messages.createSuccess')
      );

      const savedData: FormState = sanitized;
      // Update form data with saved result if available
      if (result.data) {
        const adaptedData: FormState = {
          name: result.data.name ?? sanitized.name,
          sourceType: result.data.sourceType ?? sanitized.sourceType,
          sourceConfig: result.data.sourceConfig ?? sanitized.sourceConfig,
          queryTemplate: result.data.queryTemplate ?? sanitized.queryTemplate,
          matchField: result.data.matchField ?? sanitized.matchField,
          defaultModule: result.data.defaultModule ?? sanitized.defaultModule,
          defaultDestination: result.data.defaultDestination ?? sanitized.defaultDestination,
          description: result.data.description ?? sanitized.description,
          active: Boolean(result.data.active ?? sanitized.active),
          options: Array.isArray(result.data.options)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ? result.data.options.map((opt: any, idx: number) => ({
              priority: idx + 1,
              destinationModule: opt.destinationModule ?? opt.destination_module ?? '',
              destinationValue: opt.destinationValue ?? opt.destination_value ?? '',
              condition: opt.condition ?? '',
              description: opt.description ?? '',
              matchValue: opt.matchValue ?? opt.match_value ?? '',
              enabled: opt.enabled ?? true,
            }))
            : sanitized.options,
        };
        setOriginalData(deepClone(adaptedData));
        setFormData(adaptedData);
        // Return data with ID for navigation (as any to include id/uuid)
        return { ...adaptedData, id: result.data.id, uuid: result.data.uuid } as any;
      } else {
        setOriginalData(deepClone(savedData));
        setFormData(savedData);
        // Return data with ID if available from result (as any to include id/uuid)
        return { ...savedData, id: result.data?.id, uuid: result.data?.uuid } as any;
      }
    } catch (error) {
      showErrorRef.current(error?.message || t('dynamicDestination.messages.saveError'));
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [formData, hasChanges, id, isEditMode, showSuccess, t, validateForm]);

  const handleCancel = useCallback(() => {
    if (isEditMode) {
      setFormData(deepClone(originalData));
      setErrors({});
      setOptionErrors(originalData.options.map(() => ({})));
    } else {
      const reset = getDefaultFormState();
      setFormData(reset);
      setOriginalData(deepClone(reset));
      setErrors({});
      setOptionErrors(reset.options.map(() => ({})));
    }
  }, [isEditMode, originalData]);

  return {
    formData,
    setFormData,
    originalData,
    errors,
    optionErrors,
    isSaving,
    hasChanges,
    handleFieldChange,
    handleOptionChange,
    handleDefaultModuleChange,
    handleOptionModuleChange,
    addOption,
    removeOption,
    handleSubmit,
    handleCancel,
    validateForm
  };
}

